import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const KINDS = [
  ["clients", "Clients"],
  ["campaigns", "Campaigns"],
  ["verticals", "Verticals"],
  ["managers", "Account Managers"],
  ["vias", "Vias"],
];

const blank = { name: "", email: "", phone: "", company: "", notes: "", active: true };

const MasterTable = ({ kind }) => {
  const { masters, reloadMasters } = useData();
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blank);
  const rows = masters[kind] || [];

  const save = async () => {
    if (!draft.name?.trim()) return toast.error("Name is required");
    try {
      if (editing === "new") await api.post(`/masters/${kind}`, draft);
      else await api.put(`/masters/${kind}/${editing}`, draft);
      toast.success("Saved");
      setEditing(null);
      setDraft(blank);
      reloadMasters();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/masters/${kind}/${id}`);
      toast.success("Deleted");
      reloadMasters();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const fields = ["name", "email", "phone", "notes"];

  const editRow = (
    <tr className="bg-slate-50">
      {fields.map((f) => (
        <td key={f} className="px-2 py-2">
          <Input
            data-testid={`${kind}-${f}-input`}
            placeholder={f}
            className="h-9 border-slate-300 bg-white"
            value={draft[f] || ""}
            onChange={(e) => setDraft({ ...draft, [f]: e.target.value })}
          />
        </td>
      ))}
      <td className="px-2 py-2">
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            data-testid={`${kind}-save-button`}
            className="h-9 w-9 rounded-md bg-slate-900 active:scale-95"
            onClick={save}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            data-testid={`${kind}-cancel-button`}
            className="h-9 w-9 rounded-md border-slate-300"
            onClick={() => {
              setEditing(null);
              setDraft(blank);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button
          data-testid={`add-${kind}-button`}
          className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
          onClick={() => {
            setEditing("new");
            setDraft(blank);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add
        </Button>
      </div>
      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-sm" data-testid={`${kind}-table`}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Email</th>
              <th className="px-3 py-3">Phone</th>
              <th className="px-3 py-3">Notes</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {editing === "new" && editRow}
            {rows.map((r) =>
              editing === r.id ? (
                <tr key={r.id} className="bg-slate-50">
                  {editRow.props.children}
                </tr>
              ) : (
                <tr key={r.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold">{r.name}</td>
                  <td className="px-3 py-2">{r.email || "—"}</td>
                  <td className="px-3 py-2">{r.phone || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{r.notes || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`edit-${kind}-${r.id}`}
                        className="h-8 w-8"
                        onClick={() => {
                          setEditing(r.id);
                          setDraft({ ...blank, ...r });
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`delete-${kind}-${r.id}`}
                        className="h-8 w-8 text-red-600 hover:bg-red-50"
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Masters() {
  return (
    <div data-testid="masters-page">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Configuration
        </div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Masters</h1>
      </div>
      <Tabs defaultValue="clients">
        <TabsList className="mb-6 rounded-md bg-slate-100">
          {KINDS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="rounded-md">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map(([k]) => (
          <TabsContent key={k} value={k}>
            <MasterTable kind={k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
